import React, { useState } from 'react';
import { apiService } from '@/services/api';

// Coupon-code field (Square-native discounts). The patient enters a code; we
// validate it server-side (/payments/validate-coupon) for the current cart and,
// if valid, surface the discount + discounted total. The applied coupon's `code`
// is passed to create-payment, where the BE RE-VALIDATES and applies the Square
// discount on capture (source of truth) — the FE only displays it.
// Dark behind COUPONS_ENABLED; see checkoutUi + the BE PRD.

const fmtUsd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

const CouponField = ({ planId, selectedMedIds, primaryGoal, applied, onApplied, onCleared }) => {
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle'); // idle | checking | error
  const [error, setError] = useState('');

  const apply = async () => {
    const trimmed = code.trim();
    if (!trimmed || status === 'checking') return;
    setStatus('checking');
    setError('');
    const res = await apiService.validateCoupon({ code: trimmed, planId, selectedMedIds, primaryGoal });
    if (res && res.valid) {
      setStatus('idle');
      onApplied({
        code: trimmed,
        label: res.label || 'Discount',
        discountCents: Number(res.discountCents) || 0,
        discountedTotalCents: Number(res.discountedTotalCents),
      });
    } else {
      setStatus('error');
      setError((res && res.error) || 'That code isn’t valid.');
    }
  };

  const clear = () => {
    setCode('');
    setStatus('idle');
    setError('');
    onCleared();
  };

  if (applied) {
    return (
      <div className="rounded-xl border border-[#2d8a5f]/30 bg-[#f0faf4] p-3 flex items-center justify-between" data-testid="coupon-applied">
        <div className="text-sm">
          <span className="font-medium text-gray-900">Code “{applied.code}” applied</span>
          <span className="text-[#2d8a5f] ml-2">{applied.label}{applied.discountCents ? ` · −${fmtUsd(applied.discountCents)}` : ''}</span>
        </div>
        <button type="button" onClick={clear} data-testid="coupon-remove" className="text-xs text-gray-400 hover:text-red-600">
          Remove
        </button>
      </div>
    );
  }

  return (
    <div data-testid="coupon-field">
      <label className="block text-sm font-medium text-gray-700 mb-1">Have a coupon code?</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), apply())}
          placeholder="Enter code"
          data-testid="coupon-input"
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d8a5f]/40 focus:border-[#2d8a5f]"
        />
        <button
          type="button"
          onClick={apply}
          disabled={!code.trim() || status === 'checking'}
          data-testid="coupon-apply"
          className="rounded-lg bg-[#2d8a5f] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40 hover:bg-[#246f4d]"
        >
          {status === 'checking' ? 'Checking…' : 'Apply'}
        </button>
      </div>
      {status === 'error' && (
        <p className="text-xs text-amber-700 mt-1" data-testid="coupon-error">{error}</p>
      )}
    </div>
  );
};

export default CouponField;
